import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import { Button } from '../../components/ui/button.js';
import { Badge } from '../../components/ui/badge.js';
import { Progress } from '../../components/ui/progress.js';
import { Shield, AlertTriangle, Phone, Heart, Activity, Settings, Bell, Users, Calendar, TrendingUp, CheckCircle, XCircle, Info, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext.js'; // Adjust path if needed
import { Link } from "react-router-dom";

const SafetyGuidelines = () => {
  const { user } = useAuth();

  // Extract first name using the same logic as Dashboard
  const firstName =
    user?.firstName ||
    (user?.name ? user.name.split(" ")[0] : null) ||
    user?.first_name ||
    "Friend";

  // Check if user has completed their profile
  const hasProfile = user?.profile?.fitnessLevel && 
                     user?.profile?.goals && 
                     user?.profile?.goals.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header - Enhanced */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-700 rounded-2xl shadow-xl p-8 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
            <div className="relative z-10 flex items-center gap-4">
              <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold mb-2 drop-shadow-lg">Safety Guidelines for Seniors</h1>
                <p className="text-green-100 text-lg">Your safety is our top priority</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
      
          {/* Profile Completion Prompt - Enhanced */}
          {!hasProfile && (
            <div className="mb-8 bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 border-l-4 border-green-500 rounded-xl p-6 shadow-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="bg-green-500 rounded-full p-3">
                    <AlertTriangle className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Complete Your Profile for Safer Workouts
                  </h3>
                  <p className="text-gray-700 mb-4">
                    To ensure your safety, please complete your profile with any health conditions, 
                    limitations, or concerns. This helps us provide appropriate exercise recommendations 
                    and safety warnings tailored to your specific needs.
                  </p>
                  <Link
                    to="/profile"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Update Your Profile
                  </Link>
                </div>
              </div>
            </div>
          )}
      
          {/* General Safety Resources - Enhanced */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-2">
                <Info className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800">General Safety Resources</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link 
                to="/safety/emergency" 
                className="block p-6 bg-gradient-to-br from-red-50 to-pink-50 rounded-xl border-2 border-red-200 hover:border-red-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-gradient-to-br from-red-500 to-pink-600 rounded-lg p-2">
                    <AlertTriangle className="text-white w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-red-800">Emergency Procedures</h3>
                </div>
                <p className="text-red-700">
                  Learn what to do in case of an exercise emergency, including warning signs and immediate actions to take.
                </p>
              </Link>

              <Link 
                to="/safety/faq" 
                className="block p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-2">
                    <Info className="text-white w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-800">Safety FAQ</h3>
                </div>
                <p className="text-blue-700">
                  Find answers to common safety questions and learn more about exercising safely as a senior.
                </p>
              </Link>
            </div>
          </div>
          
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg p-2">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800">Safety Guidelines</h2>
            </div>
            <p className="text-gray-700 text-lg mb-6 leading-relaxed">
              Welcome to Elderfit Secrets, <span className="font-bold text-green-700">{firstName}</span>! Your safety is our top priority. Please read and follow these guidelines before starting any exercise program.
            </p>
            <ol className="space-y-6">
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">1. Consult Your Healthcare Provider</span>
                <span className="text-gray-700 leading-relaxed">Before beginning any new exercise routine, talk to your doctor or healthcare provider, especially if you have any chronic conditions, recent surgeries, or concerns about your health.</span>
              </li>
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">2. Listen to Your Body</span>
                <span className="text-gray-700 leading-relaxed">If you feel pain, dizziness, shortness of breath, or discomfort during any exercise, stop immediately and rest. Never push through pain. Mild muscle soreness is normal, but sharp or persistent pain is not.</span>
              </li>
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">3. Start Slow and Progress Gradually</span>
                <span className="text-gray-700 leading-relaxed">Begin with beginner-level workouts and increase intensity or duration only as you feel comfortable. Allow your body time to adapt to new activities.</span>
              </li>
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">4. Warm Up and Cool Down</span>
                <span className="text-gray-700 leading-relaxed">Always start your session with a gentle warm-up to prepare your muscles and joints. Finish with a cool-down and stretching to help your body recover.</span>
              </li>
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">5. Stay Hydrated</span>
                <span className="text-gray-700 leading-relaxed">Drink water before, during, and after your workout, even if you don't feel thirsty.</span>
              </li>
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">6. Create a Safe Exercise Space</span>
                <span className="text-gray-700 leading-relaxed">Make sure your workout area is free of clutter, loose rugs, or anything you could trip over. Use a sturdy chair or support for balance exercises if needed.</span>
              </li>
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">7. Wear Appropriate Clothing and Footwear</span>
                <span className="text-gray-700 leading-relaxed">Choose comfortable, non-restrictive clothing and supportive, non-slip shoes.</span>
              </li>
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">8. Use Equipment Safely</span>
                <span className="text-gray-700 leading-relaxed">Only use equipment (like resistance bands or weights) as instructed in the app. Check that equipment is in good condition before use.</span>
              </li>
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">9. Monitor Your Health</span>
                <span className="text-gray-700 leading-relaxed">Keep track of your heart rate, blood pressure, and blood sugar if recommended by your doctor. If you feel unwell at any time, stop exercising and seek medical advice.</span>
              </li>
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">10. Exercise with a Buddy (if possible)</span>
                <span className="text-gray-700 leading-relaxed">If you're new to exercise or have balance concerns, consider having someone nearby or let a friend or family member know when you're working out.</span>
              </li>
              <li className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <span className="font-bold text-lg text-gray-900 block mb-2">11. Report Any Issues</span>
                <span className="text-gray-700 leading-relaxed">If you notice any problems with the app or have questions about exercises, use the in-app support or contact our team.</span>
              </li>
            </ol>
            
            <div className="mt-8 p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-xl shadow-md">
              <p className="text-green-800 font-bold text-lg">
                Remember: Your safety comes first. ElderFit Secrets is here to support your fitness journey—move at your own pace and enjoy the process!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyGuidelines;
